<template>
  <div>
    <VContainer v-if="product" class="px-20">
      <div class="py-5">
        <Breadcrumbs class="breadcrumbs mb-2" :breadcrumbs="product.breadcrumb" />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-16">
        <section class="md:h-full">
          <VGallery :images="product?.images" @showGallery="open" :is-open="isOpen" :product="product" />
        </section>
        <section class="flex flex-col gap-6">
          <div class="flex flex-row divide-x divide-gray-300 text-sm">
            <div class="flex items-center gap-1 pr-3">
              <span class="font-normal">Brand:</span>
              <NuxtLink
                :to="product.manufacturer.url"
                class="font-bold text-primary hover:text-primary-700 hover:underline hover:underline-offset-2 transition-colors"
              >
                {{ product.manufacturer_name }}
              </NuxtLink>
            </div>
            <div class="flex items-center gap-1 pl-3">
              <span class="font-bold">SKU:</span>
              <span class="font-semibold">{{ product.mpn }}</span>
            </div>
          </div>

          <h1 class="text-left font-bold text-2xl">{{ product.name }}</h1>

          <div class="prose" v-if="product.description_short" v-html="product.description_short" />

          <section>
            <VProductPagePrice :prices-data="pricesData" />
          </section>

          <div class="flex flex-col gap-2">
            <div v-if="product.delivery_information">
              {{ product.delivery_information }}
            </div>
            <div v-if="product.out_of_stock == '1' && !!product.delivery_out_stock">
              {{ product.delivery_out_stock }}
            </div>
            <div v-if="product.out_of_stock == '0' && !!product.delivery_in_stock">
              {{ product.delivery_in_stock }}
            </div>
          </div>

          <VProductVariant />

          <div class="flex flex-col gap-2" v-if="product?.cart_rules && product?.cart_rules.length > 0">
            <div v-for="rule in product?.cart_rules">
              <VCartRule :rule="rule" />
            </div>
          </div>

          <div v-if="product.attachments.length"  class="flex items-center gap-4 divide-x divide-gray-300">
            <div class="flex items-center gap-2">
              <svg width="15" height="18" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.22222 7.22222V12.5556M7.22222 12.5556L4.55556 9.88889M7.22222 12.5556L9.88889 9.88889M11.6667 17H2.77778C1.79594 17 1 16.2041 1 15.2222V2.77778C1 1.79594 1.79594 1 2.77778 1H7.74292C7.97867 1 8.20476 1.09365 8.37146 1.26035L13.1841 6.07298C13.3508 6.23968 13.4444 6.46578 13.4444 6.70152V15.2222C13.4444 16.2041 12.6485 17 11.6667 17Z"
                  stroke="#374151"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <a
                :href="`https://shop-ps.pogliani.com/index.php?controller=attachment&id_attachment=${product.attachments[0].id_attachment}`"
                class="text-base text-primary font-bold underline hover:text-primary-700 transition-colors"
                target="_blank"
                download
                rel="noopener noreferrer"
                >{{ $t('products.manual') }}</a
              >
            </div>
          </div>

          <hr />

          <div class="flex flex-col gap-3">
            <div class="flex flex-wrap gap-3">
              <QuantitySelector class="h-11 min-w-30!" v-model="selectedQuantity" :min="0" :max="quantitySelectable" />

              <Button :disabled="loading || !isAvailable" @click="doAddToCart" class="h-11">
                <SfProgressCircular v-show="loading" class="mr-2!" size="sm" />
                {{ addToCartText }}
              </Button>

              <!-- Product Availability Notification -->
              <ProductAvailabilityNotification
                v-if="!isAvailable"
                :product-id="product.id_product"
                :product-attribute-id="Number(product?.id_product_attribute) || undefined"
                :product-name="product.name"
              />
            </div>

            <!-- Cart status message -->
            <div v-if="quantityInCart > 0 && !isAvailable" class="space-y-2">
              <div class="bg-red-50 border border-red-200 text-red-800 p-3 rounded-md text-sm">
                {{ $t(`products.last_cart_item_text`) }}
              </div>
              <NuxtLink
                v-if="quantityInCart > 0"
                :to="$localePath('/cart')"
                class="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 text-sm font-medium underline underline-offset-2 transition-colors"
              >
                {{ $t('products.view_cart') }}
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </NuxtLink>
            </div>
          </div>

          <div class="flex flex-col gap-2 font-bold text-sm w-80">
            <AlertDialog v-for="feature in features" :key="feature.heading">
              <AlertDialogTrigger class="flex gap-3 whitespace-nowrap items-center">
                <NuxtImg
                  v-if="feature.images?.[0]?.url"
                  :src="getCmsImage(feature.images)"
                  :title="feature.images[0].title"
                  :alt="feature.images[0].alt"
                  loading="lazy"
                  class="shrink-0 w-6 h-6"
                />
                {{ feature.heading }}
              </AlertDialogTrigger>

              <AlertDialogContent class="text-center max-w-200">
                <AlertDialogHeader class="relative text-center">
                  <div class="absolute -top-2 -right-2">
                    <AlertDialogCancel
                      class="text-primary-500 border border-primary p-2 hover:text-primary-700 focus:ring-2 focus:ring-primary-300 rounded-md"
                    >
                      <span class="sr-only">Close</span>
                      <SfIconClose class="text-primary-500 shrink-0" size="2xl" />
                    </AlertDialogCancel>
                  </div>

                  <AlertDialogTitle class="mt-6 px-4">
                    <h1 class="text-xl font-semibold text-center">{{ feature.heading }}</h1>
                  </AlertDialogTitle>

                  <AlertDialogDescription class="px-4">
                    <div v-html="feature.body?.html" class="text-sm text-gray-600 text-center" />
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter v-if="feature.callToAction">
                  <div class="w-full flex justify-center">
                    <AlertDialogAction
                      class="mt-2"
                      title="feature.callToAction.title"
                      :href="feature.callToAction.url"
                      target="feature.callToAction.target"
                      rel="noopener noreferrer"
                    >
                      <!-- {{ $t ('products.featureActionText','Find out more') }} -->
                      {{ feature.callToAction.label }}
                    </AlertDialogAction>
                  </div>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <CheckoutPaymentIcons class="w-full" />

        </section>
      </div>
    </VContainer>

    <div class="bg-gray-100 py-8">
      <VContainer>
        <div class="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
          <div class="space-y-8">
            <Card class="w-full">
              <CardContent class="p-4 sm:p-6 md:p-10 flex-col justify-start">
                <VSection :title="$t('Description')">
                  <ClientOnly>
                    <div class="prose max-w-4xl" v-html="product?.description.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')" />
                  </ClientOnly>
                </VSection>
                <VSection :title="$t('Details')" class="mt-8">
                  <dl class="divide-y">
                    <div class="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt class="text-sm font-medium text-gray-500">
                        {{ $t('Category') }}
                      </dt>
                      <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                        {{ product?.category_name }}
                      </dd>
                    </div>
                    <div
                      class="py-2 sm:grid sm:grid-cols-3 sm:gap-4"
                      v-for="(property, i) in product?.product_info"
                      :key="`property-${i}`"
                    >
                      <dt class="text-sm font-medium text-gray-500">
                        {{ $t(property.name) }}
                      </dt>
                      <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                        {{ property.value }}
                      </dd>
                    </div>
                  </dl>
                </VSection>
              </CardContent>
            </Card>

            <!-- Product Reviews Section -->
            <ProductReviews :product-id="product?.id_product ?? 0" />
          </div>
          <div>
            <Card class="hidden md:block w-full sticky top-0">
              <CardContent class="flex flex-col justify-start gap-6 p-4 md:p-6">
                <Card class="aspect-square bg-white">
                  <NuxtImg
                    v-if="image"
                    :src="image"
                    format="webp"
                    :alt="product?.name"
                    loading="lazy"
                    class="aspect-square object-cover w-full"
                  />
                </Card>
                <VProductPagePrice :prices-data="pricesData" />
                <VProductVariant />
                <div class="flex flex-col gap-3">
                  <div class="flex flex-wrap gap-3">
                    <QuantitySelector class="h-11" v-model="selectedQuantity" :min="0" :max="quantitySelectable" />
                    <Button :disabled="loading || !isAvailable" @click="doAddToCart" class="h-11">
                      <SfProgressCircular v-show="loading" class="mr-4" />
                      {{ addToCartText }}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </VContainer>
    </div>

    <VContainer>
      <LazyVisibleVRelatedProducts
        hydrate-on-visible
        class="mt-8"
        :heading="$t('products.related_products', 'Related Products')"
        :id_product="product?.id_product"
      />

      <LazyVisibleRelatedProducts
        hydrate-on-visible
        class="mt-8"
        :heading="$t('products.category_products', 'Category Products')"
        :id_category_default="Number(product?.id_category_default)"
      />
    </VContainer>

  </div>

  <!-- Preorder Confirmation Modal -->
  <PreorderConfirmationModal
    v-model:open="showPreorderModal"
    :loading="loading"
    @confirm="confirmPreorder"
  />

  <!-- Backorder Confirmation Modal -->
  <BackorderConfirmationModal
    v-model:open="showBackorderModal"
    :loading="loading"
    :requested-quantity="selectedQuantity"
    :available-quantity="Number(product?.quantity || 0)"
    @confirm="confirmBackorder"
  />
</template>

<script setup lang="ts">
import { SfIconClose, SfProgressCircular, useDisclosure } from '@storefront-ui/vue';
import { defineProduct, useSchemaOrg, defineBreadcrumb } from '@unhead/schema-org/vue';
import { Button } from '~/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import PreorderConfirmationModal from '~/components/PreorderConfirmationModal.vue';
import BackorderConfirmationModal from '~/components/BackorderConfirmationModal.vue';
import { getCmsImage } from '@/composables/useCms';
import { useAddToCartText } from '~/composables/useAddToCart';

const config = useRuntimeConfig();
const { data: product, refresh: fetchProduct } = useProduct();
const { isOpen, open } = useDisclosure({ initialValue: false });
const gtm = useGtm();
const { t } = useI18n();
const { features } = useProductFeatures();

const image = computed(() => product.value?.default_image?.url.replace('home_default', 'large_default'));

onMounted(() => {
  gtm?.trackEvent({
    event: 'view_item',
    ecommerce: {
      value: product.value?.price_amount,
      items: [
        {
          google_business_vertical: 'retail',
          item_id:
            product.value?.id_product + (product.value?.id_product_attribute ? '-' + product.value?.id_product_attribute : ''),
          item_brand: product.value?.manufacturer_name,
          price: product.value?.price_amount,
        },
      ],
    },
  });
});

await fetchProduct();

const { text: addToCartText } = useAddToCartText(() : any => product.value);
const { quantityInCart, quantitySelectable, isAvailable } = useCartItem(product);

const title = computed(() => product.value?.name ?? '');

const selectedQuantity = ref(1);

const {
  loading,
  showPreorderModal,
  showBackorderModal,
  doAddToCart,
  confirmPreorder,
  confirmBackorder,
} = useProductAddToCart(product, selectedQuantity, {
  afterAddToCart: fetchProduct,
});

useHead({
  title: title.value,
  meta: [],
  link: [
    {
      rel: 'canonical',
      href: product.value?.product_url,
    },
    ...Object.keys(product.value?.alternative_urls || {}).map((lang) => {
      return {
        rel: 'alternate',
        hreflang: lang,
        href: product.value?.alternative_urls[lang as keyof typeof product.value.alternative_urls],
      };
    }),
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: product.value?.alternative_urls?.['en-us'],
    },
  ],
});

useSeoMeta({
  title: product.value?.name,
  ogTitle: product.value?.name,
  description: product.value?.meta_description,
  ogDescription: product.value?.meta_description,
  ogImage: product.value?.default_image?.url,
});

let schema = [];

schema.push(
  defineBreadcrumb({
    itemListElement:
      product.value?.breadcrumb.map((b) => ({
        name: b?.title,
        item: b.url.replace(useRuntimeConfig().public.prestashop_url, config.public.app_url),
      })) ?? [],
    numberOfItems: product.value?.breadcrumb?.length ?? 0,
  }),
);

// Helper function to get proper availability schema URL
const getAvailability = (quantity: number, availableForOrder: boolean) => {
  if (quantity > 0) return 'https://schema.org/InStock';
  if (availableForOrder) return 'https://schema.org/PreOrder';
  return 'https://schema.org/OutOfStock';
};

// Set priceValidUntil to 1 year from now
const priceValidUntil = new Date();
priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

if (product.value?.variants && product.value?.variants.length > 0) {
  // Build hasVariant array with all variant products
  const hasVariant = [];

  for (let index = 0; index < product.value.variants.length; index++) {
    const element = product.value.variants[index];
    const variantProduct: any = {
      '@type': 'Product',
      '@id': `#variant_${index}`,
      name: `${product.value?.name} - ${element.attribute_name}`,
      image: image.value,
      description: product.value?.meta_description,
      size: element.attribute_name,
      brand: {
        '@type': 'Brand',
        name: product.value?.manufacturer_name ?? '',
      },
      offers: {
        '@type': 'Offer',
        price: String(element.price || product.value?.price_amount),
        priceCurrency: product.value?.currency_iso_code,
        url: element.url,
        priceValidUntil: priceValidUntilStr,
        availability: element.availability || getAvailability(element.quantity || 0, product.value?.available_for_order == '1'),
        itemCondition: 'https://schema.org/NewCondition',
      },
    };

    // Add product identifiers (at least one is required)
    if (element.reference) {
      variantProduct.sku = element.reference;
      variantProduct.mpn = element.reference;
    }
    if (element.ean13) {
      variantProduct.gtin13 = element.ean13;
    }

    hasVariant.push(variantProduct);
  }

  // Add ProductGroup with hasVariant array
  schema.push({
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    '@id': '#product_group',
    name: product.value?.name,
    description: product.value?.meta_description,
    image: image.value,
    url: product.value?.product_url,
    brand: {
      '@type': 'Brand',
      name: product.value?.manufacturer_name ?? '',
    },
    productGroupID: product.value?.id_product,
    variesBy: ['https://schema.org/size'],
    hasVariant: hasVariant,
  });
} else {
  const productData: any = {
    name: product.value?.name ?? '',
    description: product.value?.meta_description,
    image: image.value,
    brand: {
      '@type': 'Brand',
      name: product.value?.manufacturer_name ?? '',
    },
    offers: [
      {
        '@type': 'Offer',
        price: String(product.value?.price_amount),
        priceCurrency: product.value?.currency_iso_code,
        url: product.value?.product_url,
        priceValidUntil: priceValidUntilStr,
        availability: getAvailability(product.value?.quantity ?? 0, product.value?.available_for_order == '1'),
        itemCondition: 'https://schema.org/NewCondition',
      },
    ],
  };

  // Add product identifiers (at least one is required)
  if (product.value?.reference) {
    productData.sku = product.value.reference;
  }
  if (product.value?.mpn) {
    productData.mpn = product.value.mpn;
  }
  if (product.value?.ean13) {
    productData.gtin13 = product.value.ean13;
  }
  if (product.value?.category_name) {
    productData.category = product.value.category_name;
  }

  schema.push(defineProduct(productData));
}

useSchemaOrg(schema);

const { data: pricesData } = usePricesData(product);
</script>

<style scoped>
.prose h1,
.prose h2,
.prose h3 {
  font-family: var(--font-family-primary);
  margin-bottom: 0.5rem;
  line-height: var(--line-height-default);
}

.prose h1 {
  font-size: var(--h1-size);
  font-weight: var(--font-bold);
}

.prose h2 {
  font-size: var(--h2-size);
  font-weight: var(--font-semibold);
}

.prose h3 {
  font-size: var(--h3-size);
  font-weight: var(--font-semibold);
}

.prose p,
.prose span,
.prose table,
.prose ul,
.prose ol {
  font-family: var(--font-family-secondary);
  font-size: var(--paragraph-size);
  font-weight: var(--font-regular);
  line-height: var(--line-height-default);
  color: var(--paragraph-color);
}

.prose table {
  border-collapse: collapse;
  width: 100%;
  margin-top: 1rem;
}

.prose table th,
.prose table td {
  padding: 0.5rem;
  text-align: left;
  border: 1px solid var(--paragraph-color);
}
</style>
